import {
  Box,
  Button,
  FormGroup,
  Modal,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { CorrectionData, CorrectionDataError, UserApiData } from '../../types';
import { useAPI } from '../../hooks/useAPI';
import { useEffect, useState } from 'react';
import { useConfirmDialog } from '../../hooks/useConfirmDialog';
import { ConfirmDialog } from '../../components/global/ConfirmDialog';
import { useAuth } from '../../contexts/AuthContext';

interface CorrectionModalProps {
  open: boolean;
  handleClose: () => void;
  handleSave: () => void;
  correction: CorrectionData;
}

export const CorrectionModal = ({
  open,
  handleClose,
  handleSave,
  correction,
}: CorrectionModalProps) => {
  const [mCorrection, setMCorrection] = useState<CorrectionData>(correction);
  const [error, setError] = useState<CorrectionDataError>({});
  const { fetchData } = useAPI();
  const { isOpen, config, openDialog, closeDialog } = useConfirmDialog();
  const [userApi, setUserApi] = useState<UserApiData>({});
  const { isAuthenticated } = useAuth();

  useEffect(() => {
    if (isAuthenticated) {
      fetchData('/user-api').then(({ errors, data }) => {
        setUserApi(data);
      });
    }
  }, [isAuthenticated]);

  useEffect(() => {
    setMCorrection({ ...correction });
  }, [correction]);

  const onSaveCorrection = async () => {
    try {
      JSON.parse(mCorrection.information);
    } catch (e) {
      setError({
        ...error,
        information: ['The information field must be a json.'],
      });

      return;
    }

    const requestData = {
      id: correction.id,
      public_key: userApi.public_key,
      secret_key: userApi.secret_key,
      job_id: mCorrection.job_id,
      information: JSON.parse(mCorrection.information),
    };

    const { errors, data } = await fetchData(
      '/private/correction',
      requestData,
      'POST',
    );
    if (errors) {
      setError(errors);
    } else {
      handleSave();
    }
  };

  const onDeleteCorrection = async () => {
    const { errors, data } = await fetchData(
      '/corrections/delete',
      { id: mCorrection.id },
      'POST',
    );
    if (errors) {
      setError(errors);
    } else {
      handleSave();
    }
  };

  return (
    <Modal
      open={open}
      // onClose={handleClose}
      aria-labelledby="modal-title"
      aria-describedby="modal-description">
      <Box
        sx={{
          position: 'absolute',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          width: '80%',
          maxWidth: '750px',
          bgcolor: 'background.paper',
          border: '1px solid #909894',
          boxShadow: '0px 4px 4px 0px #00000040',
          borderRadius: '16px',
          p: 4,
          maxHeight: '90%',
          overflow: 'auto',
        }}>
        <Typography
          id="modal-title"
          variant="h4"
          component="h2"
          fontWeight="600">
          {correction.id ? 'Edit Correction' : 'Add Correction'}
        </Typography>
        <Stack mt={4} spacing={4}>
          <FormGroup>
            <Typography fontWeight="600">Job Ref</Typography>
            <TextField
              variant="outlined"
              value={mCorrection.job_id}
              error={error.job_id ? true : false}
              helperText={error?.job_id?.[0] ?? ''}
              onChange={e =>
                setMCorrection({ ...mCorrection, job_id: e.target.value })
              }
              autoFocus
            />
          </FormGroup>
          <FormGroup>
            <Typography fontWeight="600">Information</Typography>
            <TextField
              variant="outlined"
              multiline
              rows={6}
              value={mCorrection.information}
              error={error.information ? true : false}
              helperText={error?.information?.[0] ?? ''}
              placeholder='{"Serial Number": "123456789", "Manufacturer": "Dell"}'
              onChange={e =>
                setMCorrection({ ...mCorrection, information: e.target.value })
              }
              sx={{
                width: '100%',
                height: 'auto',
                '& .MuiInputBase-root': { height: 'auto' },
              }}
              autoFocus
            />
          </FormGroup>
          <FormGroup>
            <Stack direction="row" spacing={2}>
              <Button
                variant="contained"
                size="large"
                onClick={() => onSaveCorrection()}>
                Save Correction
              </Button>
              {correction.id && (
                <Button
                  variant="outlined"
                  size="large"
                  color="error"
                  onClick={() =>
                    openDialog(
                      'Confirm Deletion',
                      `Are you sure you want to delete the correction?`,
                      () => onDeleteCorrection(),
                    )
                  }>
                  Delete Correction
                </Button>
              )}
              <Button variant="text" size="large" onClick={handleClose}>
                Cancel
              </Button>
            </Stack>
          </FormGroup>
        </Stack>
        <ConfirmDialog
          open={isOpen}
          title={config.title}
          description={config.description}
          onConfirm={config.onConfirm}
          onClose={closeDialog}
        />
      </Box>
    </Modal>
  );
};
